import { useEffect, useState } from 'react';
import { Mic } from 'lucide-react';
import { api } from '../../api';
import { cn } from '../../lib/utils';

export const PLACES = [
  { id: 'bureau', icon: '🏢', label: 'Bureau' },
  { id: 'maison', icon: '🏠', label: 'Maison' },
  { id: 'voiture', icon: '🚗', label: 'Voiture' },
  { id: 'ailleurs', icon: '📍', label: 'Ailleurs' },
];
const MINUTES = [5, 15, 30, 60];
const NEEDS = [{ id: 'pc', label: 'PC' }, { id: 'ebanking', label: 'e-banking' }, { id: 'telephone', label: 'Téléphone' }];

export const sessionLabel = (d = new Date()) => (d.getHours() < 11 ? 'matin' : d.getHours() < 15 ? 'midi' : 'libre');

// Suggestion tirée de la semaine type (jamais de l'appareil) : affichée en pointillé, jamais présélectionnée.
function agendaSuggestion(d = new Date()) {
  const day = d.getDay(), h = d.getHours() + d.getMinutes() / 60;
  if (day >= 1 && day <= 5 && h >= 9 && h < 17.5) return 'bureau';
  if (day === 1 && h >= 18) return 'ailleurs'; // lundi soir : privés à Genève
  return null;
}

const Chip = ({ on, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={cn('px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors',
      on ? 'bg-[#0288D1] border-[#0288D1] text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300')}>
    {children}
  </button>
);

export default function SessionStart({ onStart, busy }) {
  const label = sessionLabel();
  const suggestion = agendaSuggestion();
  const [place, setPlace] = useState(null);
  const [minutes, setMinutes] = useState(15);
  const [energy, setEnergy] = useState('en_forme');
  const [needs, setNeeds] = useState(['pc', 'telephone']);

  useEffect(() => {
    api.get(`/cockpit/last-choice?label=${label}`).then(c => {
      if (c.minutes) setMinutes(c.minutes);
      if (c.energy) setEnergy(c.energy);
      if (c.needs) setNeeds(c.needs);
    }).catch(() => {});
  }, [label]);

  const toggleNeed = (id) => setNeeds(n => (n.includes(id) ? n.filter(x => x !== id) : [...n, id]));
  const start = () => place && onStart({ place, minutes, energy, needs });
  const title = { matin: 'Session du matin', midi: 'Session de midi', libre: 'Session libre' }[label];

  return (
    <div className="max-w-xl mx-auto bg-white rounded-2xl border border-slate-100 shadow-sm p-5 lg:p-6 space-y-5" data-testid="session-start">
      <div>
        <h2 className="text-xl font-extrabold text-slate-900 font-manrope">{title}</h2>
        <p className="text-sm text-slate-500">Où es-tu ? Je prépare la suite selon ta réponse.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {PLACES.map(p => (
          <button key={p.id} type="button" onClick={() => setPlace(p.id)} data-testid={`place-${p.id}`}
            className={cn('rounded-2xl border p-4 text-center font-bold text-slate-800 transition-colors',
              place === p.id ? 'border-[#0288D1] bg-[#0288D1]/10 text-[#0288D1]'
                : suggestion === p.id ? 'border-2 border-dashed border-[#0288D1]/60 bg-white' : 'border-slate-200 bg-white hover:border-slate-300')}>
            <span className="block text-2xl mb-1">{p.icon}</span>{p.label}
          </button>
        ))}
      </div>
      {suggestion && !place && <p className="text-xs text-slate-400 -mt-3">Cadre pointillé = ce que dit ta semaine type. C'est toi qui choisis.</p>}
      <div className="space-y-3">
        <Row label="J'ai">{MINUTES.map(m => <Chip key={m} on={minutes === m} onClick={() => setMinutes(m)}>{m === 60 ? '1 h+' : `${m} min`}</Chip>)}</Row>
        <Row label="Énergie">
          <Chip on={energy === 'en_forme'} onClick={() => setEnergy('en_forme')}>💪 En forme</Chip>
          <Chip on={energy === 'fatigue'} onClick={() => setEnergy('fatigue')}>😴 Fatigué</Chip>
        </Row>
        <Row label="Sous la main">{NEEDS.map(n => <Chip key={n.id} on={needs.includes(n.id)} onClick={() => toggleNeed(n.id)}>{n.label}</Chip>)}</Row>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <button type="button" onClick={start} disabled={!place || busy} data-testid="start-session"
          className="rounded-2xl bg-[#26A69A] hover:bg-[#219e92] text-white font-bold py-3 disabled:opacity-40">
          {place ? 'Commencer' : 'Choisis où tu es'}
        </button>
        <button type="button" disabled title="Bientôt : après la mise en ligne"
          className="rounded-2xl bg-[#0288D1]/10 text-[#0288D1] font-bold py-3 flex items-center justify-center gap-2 opacity-70">
          <Mic size={16} /> En conversation · bientôt
        </button>
      </div>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="w-24 text-xs font-semibold text-slate-500">{label}</span>{children}
  </div>
);
